import { useQuery } from 'react-query';

import { Button, Spinner } from 'modules/v2/common/AtomicDesign/atoms';
import { getModAssets } from 'modules/api/digitalServices/mailOnDemand';
import { cn } from 'modules/v2/common/utils/cn';

import strings from '../../strings';

const assetKey = (asset) => `${asset.kind}:${asset.sourceId}`;

const AssetStep = ({ selectedAsset, onSelectAsset, onNext }) => {
  const { data: assets, isLoading, isError, refetch } = useQuery('modAssets', getModAssets);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <Spinner />
        <span className="text-neutral-500">{strings.assets.loading}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 py-6">
        <p className="text-neutral-800">{strings.assets.loadError}</p>
        <Button color="outlined" onClick={() => refetch()}>
          {strings.common.retry}
        </Button>
      </div>
    );
  }

  if (!assets || assets.length === 0) {
    return (
      <div className="py-12 text-center" data-testid="mod-assets-empty">
        <h3 className="mb-2 text-lg font-semibold">{strings.assets.emptyTitle}</h3>
        <p className="mx-auto max-w-lg text-neutral-500">{strings.assets.emptyBody}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold">{strings.assets.title}</h2>
      <p className="mb-6 text-neutral-500">{strings.assets.subtitle}</p>

      {/* Masonry (CSS columns): covers show in full at their natural ratio
          instead of being cropped to a fixed height. Each card avoids
          breaking across columns; media is letterboxed within a bound so a
          tall/large asset can't dominate the grid. */}
      <div className="columns-1 gap-4 sm:columns-2 lg:columns-3">
        {assets.map((asset) => {
          const isSelected = selectedAsset && assetKey(selectedAsset) === assetKey(asset);
          // Member-facing preview: the digital-book read page (task 35) when
          // present, else the generated preview/PDF.
          const previewHref =
            asset.previewPageUrl || asset.previewUrl || asset.pdfUrl;

          return (
            <button
              type="button"
              key={assetKey(asset)}
              data-testid={`mod-asset-${asset.sourceId}`}
              onClick={() => onSelectAsset(asset)}
              className={cn(
                'mb-4 block w-full break-inside-avoid overflow-hidden rounded-lg border bg-white text-left transition-colors',
                isSelected
                  ? 'border-neutral-800 ring-2 ring-neutral-800'
                  : 'border-neutral-200 hover:border-neutral-400',
              )}
            >
              {asset.thumbnailUrl ? (
                <div className="flex items-center justify-center bg-neutral-100">
                  <img
                    src={asset.thumbnailUrl}
                    alt={asset.title}
                    loading="lazy"
                    className="max-h-[420px] w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-32 w-full items-center justify-center bg-neutral-100 text-neutral-400">
                  {asset.kind === 'book' ? strings.assets.kindBook : strings.assets.kindTbExport}
                </div>
              )}
              <div className="p-3">
                <div className="font-medium text-neutral-800">{asset.title}</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="rounded bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold text-neutral-600">
                    {asset.kind === 'book'
                      ? strings.assets.kindBook
                      : strings.assets.kindTbExport}
                  </span>
                  {asset.printReady === false && (
                    <span className="text-[11px] text-amber-700">
                      {strings.assets.beingPrepared}
                    </span>
                  )}
                </div>
                {previewHref && (
                  <a
                    href={previewHref}
                    target="_blank"
                    rel="noreferrer noopener"
                    onClick={(event) => event.stopPropagation()}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-700"
                    data-testid={`mod-asset-preview-${asset.sourceId}`}
                  >
                    {asset.kind === 'book'
                      ? strings.assets.previewBook
                      : strings.assets.previewPdf}
                  </a>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex justify-end">
        <Button disabled={!selectedAsset} onClick={onNext} data-testid="mod-asset-next">
          {strings.common.next}
        </Button>
      </div>
    </div>
  );
};

export default AssetStep;
